import app from 'flarum/forum/app';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import extractText from 'flarum/common/utils/extractText';
import AutoReloadingPage from './AutoReloadingPage';
import CartothequeEntryEditModal from './CartothequeEntryEditModal';
import ExportLinkButton from './ExportLinkButton';
import ListEnd from './ListEnd';
import cardLabels from '../utils/cardLabels';
import PagedList from '../utils/PagedList';
import { phylumLabel, phylumRank } from '../utils/phylum';
import { loginRequiredPlaceholder } from '../utils/requireLogin';
// Cards a page, as the list scrolls; the API's maximum page.
const PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 2000;

// The phylum filter's "cards without one" choice - '' is every card.
const NO_PHYLUM = '-';

// The whole cartothèque in a few figures, its filters aside - see
// CartothequeEntryResource::summary().

/**
 * /societies/:id/cartotheque - one société's cartothèque, split out of
 * SocietyPage per explicit feedback (that page's own management screen was
 * getting crowded, and this needs its own direct link from ExhibitionPage
 * too, for creating a card on the spot while logging a finding that isn't
 * carded yet). Reuses SocietyPage's own ChampisSocietyPage-section*
 * classes as-is - same "genuinely the same element/behavior, not just
 * visually similar" reasoning as that file's own reuse of them for
 * ExhibitionsListPage's per-société sections. Toxicities are still needed
 * here (not just on SocietyPage) - each entry shows its own toxicity badge,
 * and CartothequeEntryEditModal's own toxicity picker needs the full list.
 *
 * The cards come a page at a time as the list scrolls (PagedList,
 * ListEnd), searched, filtered and in the société's own order - by number
 * then name, or by phylum (Society.cartothequeSortByPhylum()) - by the
 * server (CartothequeEntryResource), as its exports and printed cards are
 * (CartothequeOrder). What the filters and buttons need of the whole
 * cartothèque - how many cards, to print, to correct, the phyla and the
 * span of numbers in use - comes with the first page (its summary), and
 * each card to correct says what the correction would duplicate
 * (suggestionConflicts). After a change, the cards shown are loaded again
 * where they are.
 */
export default class CartothequePage extends AutoReloadingPage {
  society = null;
  toxicities = null;
  entries = (() => new PagedList('champis-exhibitions-cartotheque-entries', () => this.listQuery(), {
    pageSize: PAGE_SIZE,
    maxPageSize: MAX_PAGE_SIZE,
    onload: () => this.syncRange()
  }))();
  cartothequeSearch = '';
  onlyToPrint = false;
  onlyInvalid = false;
  phylumFilter = '';
  noBoundsMin = null;
  noBoundsMax = null;
  noRangeMin = null;
  noRangeMax = null;
  resetOnCreate() {
    this.cartothequeSearch = '';
    this.onlyToPrint = false;
    this.onlyInvalid = false;
    this.phylumFilter = '';
    // All null until the first page's summary gives the span of numbers in
    // use (see syncRange()) - noBounds* are the slider's own min/max
    // attributes, noRange* the sub-range currently selected within that
    // span, the whole span at first (i.e. no filtering effect) and then
    // moved independently as the user drags a handle.
    this.noBoundsMin = null;
    this.noBoundsMax = null;
    this.noRangeMin = null;
    this.noRangeMax = null;
  }
  resetData() {
    this.society = null;
    this.toxicities = null;
    this.entries.clear();
  }
  currentId() {
    return this.society && this.society.id();
  }

  // SocietyResource's own Show endpoint is ->authenticated() - a guest's
  // doomed request is already skipped one level up, in AutoReloadingPage's
  // own load() (see requireLogin.js's own docblock).
  fetch() {
    return app.store.find('champis-exhibitions-societies', m.route.param('id'));
  }
  onLoaded(society) {
    this.society = society;
    app.setTitle(extractText(app.translator.trans('champis-exhibitions.forum.cartotheque_page.page_title', {
      society: society.name()
    })));
    m.redraw();
    this.loadToxicities();
    this.entries.load();
  }
  onremove(vnode) {
    super.onremove(vnode);
    this.entries.clear();
  }
  loadToxicities() {
    this.toxicities = null;
    m.redraw();
    app.store.find('champis-exhibitions-toxicities', {
      filter: {
        society: this.society.id()
      },
      sort: 'name'
    }).then(toxicities => {
      this.toxicities = toxicities;
      m.redraw();
    });
  }

  // The cards as the filters ask, in the société's own order.
  listQuery() {
    const society = this.society;
    const search = this.cartothequeSearch.trim();
    const numbers = this.summary()?.numbers;
    // Only a range narrowed from the whole span filters: the cards without
    // a number stay until then.
    const narrowed = !!numbers && this.noRangeMin !== null && this.noRangeMax !== null && (this.noRangeMin > numbers.min || this.noRangeMax < numbers.max);
    return {
      filter: {
        society: society.id(),
        ...(search ? {
          q: search
        } : {}),
        ...(this.onlyToPrint ? {
          toPrint: 1
        } : {}),
        ...(this.onlyInvalid ? {
          invalid: 1
        } : {}),
        ...(this.phylumFilter ? {
          phylum: this.phylumFilter
        } : {}),
        ...(narrowed ? {
          noFrom: this.noRangeMin,
          noTo: this.noRangeMax
        } : {})
      },
      sort: society.cartothequeSortByPhylum() ? 'phylum' : 'number'
    };
  }

  // The whole cartothèque in a few figures, from the last list loaded.
  summary() {
    return this.entries.meta?.summary;
  }

  // The range slider's span: the numbers in use (their leading number,
  // "700a" being 700). A range left whole follows the span - a card
  // numbered further, the last of its numbers deleted; a narrowed one stays
  // within it.
  syncRange() {
    const numbers = this.summary()?.numbers ?? null;
    const whole = this.noRangeMin === null || this.noRangeMin === this.noBoundsMin && this.noRangeMax === this.noBoundsMax;
    this.noBoundsMin = numbers?.min ?? null;
    this.noBoundsMax = numbers?.max ?? null;
    if (!numbers) {
      this.noRangeMin = null;
      this.noRangeMax = null;
    } else if (whole) {
      this.noRangeMin = numbers.min;
      this.noRangeMax = numbers.max;
    } else {
      this.noRangeMin = Math.min(Math.max(this.noRangeMin, numbers.min), numbers.max);
      this.noRangeMax = Math.max(Math.min(this.noRangeMax, numbers.max), this.noRangeMin);
    }
  }
  view() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.cartotheque_page.${key}`, params);
    };
    if (this.requiresLogin) {
      return m("div", {
        className: "Page ChampisSocietyPage"
      }, m("div", {
        className: "container"
      }, loginRequiredPlaceholder(t('login_required'))));
    }
    if (this.error) {
      return m("div", {
        className: "Page ChampisSocietyPage"
      }, m("div", {
        className: "container"
      }, m(Placeholder, {
        text: t('error')
      })));
    }
    if (!this.society) {
      return m("div", {
        className: "Page ChampisSocietyPage"
      }, m("div", {
        className: "container"
      }, m(LoadingIndicator, null)));
    }

    // Where "back" goes depends on how this page was reached - carried as
    // query params on the link itself (see ExhibitionPage.js/
    // ExhibitionsListPage.js's own cartotheque links), since the société
    // id alone (the route's own :id) can't tell an exhibition or the
    // societies list apart from société management. Falls back to the
    // société page itself when neither is present (SocietyPage's own
    // link, or a bookmarked/direct URL).
    const exhibitionId = m.route.param('exhibition');
    const fromList = m.route.param('from') === 'list';
    let backHref;
    let backLabelKey;
    if (exhibitionId) {
      backHref = app.route('exhibition', {
        id: exhibitionId
      });
      backLabelKey = 'back_to_exhibition';
    } else if (fromList) {
      backHref = app.route('exhibitions');
      backLabelKey = 'back_to_exhibitions';
    } else {
      backHref = app.route('society', {
        id: this.society.id()
      });
      backLabelKey = 'back_to_society';
    }
    return m("div", {
      className: "Page ChampisSocietyPage"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "ChampisDiscussionList-header"
    }, m(Link, {
      href: backHref,
      className: "ChampisExhibitions-backLink ChampisExhibitions-backLink--prominent"
    }, m("i", {
      className: "icon fas fa-chevron-left",
      "aria-hidden": "true"
    }), " ", t(backLabelKey)), m("div", {
      className: "ChampisExhibitions-headerRow"
    }, m("div", null, m("i", {
      className: "icon fas fa-address-card",
      "aria-hidden": "true"
    }), " ", t('page_title', {
      society: this.society.name()
    })), m("div", {
      className: "ChampisSocietyPage-sectionHeaderActions"
    }, m(Button, {
      icon: "fas fa-plus",
      className: "Button Button--primary",
      onclick: () => this.openAddEntryModal()
    }, t('add_entry_button'))))), this.cartothequeBody()));
  }
  cartothequeBody() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.cartotheque_page.${key}`, params);
    };
    const apiUrl = app.forum.attribute('apiUrl');
    const entries = this.entries;
    const summary = this.summary();
    // What the cards shown were searched for.
    const searched = entries.currentParams.filter?.q;
    // The phyla actually in use, in their own order - the filter only
    // offers those (and "without", when some cards have none).
    const phyla = (summary?.phyla || []).slice().sort((a, b) => phylumRank(a) - phylumRank(b) || a.localeCompare(b));
    const hasNoPhylum = !!summary?.withoutPhylum;
    // Of the whole cartothèque - "nothing flagged" doesn't depend on
    // whatever's currently typed into the search box or the "à imprimer"
    // toggle below.
    const flaggedCount = summary?.toPrint ?? 0;
    // Cards whose species is no longer GBIF's accepted name - the ones with
    // the "Espèce non valide" banner (suggestedSpecies, set by
    // champis-exhibitions:check-cartotheque-species).
    const invalidCount = summary?.invalid ?? 0;
    // Only offered at all when at least two distinct numbers are actually
    // in use (see syncRange() for where these get set) - a société that
    // doesn't number its cards (both still null) or where every card
    // shares the same one number has nothing for a *range* slider to
    // meaningfully act on.
    const hasNoRange = this.noBoundsMin !== null && this.noBoundsMax !== null && this.noBoundsMin !== this.noBoundsMax;
    // The visible "selected range" bar drawn behind the two invisible-track
    // sliders (see forum.less) - both native inputs only ever draw their
    // own thumb, so this fill is the only actual visual feedback for
    // which portion of the full span is currently selected.
    const noRangeFillStyle = hasNoRange && {
      left: `${(this.noRangeMin - this.noBoundsMin) / (this.noBoundsMax - this.noBoundsMin) * 100}%`,
      right: `${100 - (this.noRangeMax - this.noBoundsMin) / (this.noBoundsMax - this.noBoundsMin) * 100}%`
    };
    return m("div", {
      className: "ChampisSocietyPage-sectionBody"
    }, m("div", {
      className: "ChampisSocietyPage-cartothequeActions"
    }, m(ExportLinkButton, {
      icon: "fa-print",
      label: t('download_flagged_button'),
      title: flaggedCount === 0 ? t('download_flagged_empty') : t('download_flagged_button'),
      disabled: flaggedCount === 0,
      href: `${apiUrl}/champis-exhibitions-societies/${this.society?.id()}/cartotheque/pdf?allCards=false`,
      onBeforeClick: () => {
        // Same confirm-before-a-hard-to-undo-action pattern as this
        // page's own delete confirmations - MergeCardsPdfController
        // clears to_print on every non-allCards download, so
        // clicking through is a real "yes, un-flag these" decision,
        // not just a plain download.
        if (!confirm(extractText(t('download_flagged_confirm')))) return false;
        this.downloadFlaggedCards();
        return true;
      }
    }), m(ExportLinkButton, {
      icon: "fa-id-card",
      label: t('download_all_button'),
      href: `${apiUrl}/champis-exhibitions-societies/${this.society?.id()}/cartotheque/pdf?allCards=true`
    }), m(ExportLinkButton, {
      icon: "fa-file-excel",
      label: t('export_list_xlsx_button'),
      href: `${apiUrl}/champis-exhibitions-societies/${this.society?.id()}/cartotheque/list.xlsx`
    }), m(ExportLinkButton, {
      icon: "fa-file-pdf",
      label: t('export_list_pdf_button'),
      href: `${apiUrl}/champis-exhibitions-societies/${this.society?.id()}/cartotheque/list.pdf`
    })), summary && summary.count > 0 && m("div", {
      className: "ChampisSocietyPage-cartothequeFilters"
    }, m("input", {
      className: "FormControl ChampisSocietyPage-search",
      type: "search",
      placeholder: extractText(t('search_placeholder')),
      "aria-label": extractText(t('search_placeholder')),
      value: this.cartothequeSearch,
      oninput: e => {
        this.cartothequeSearch = e.target.value;
        entries.reloadSoon();
      }
    }), m("div", {
      className: "ChampisSocietyPage-cartothequeFiltersRow"
    }, m("label", {
      className: "ChampisSocietyPage-onlyToPrintFilter"
    }, m("input", {
      type: "checkbox",
      checked: this.onlyToPrint,
      onchange: e => {
        this.onlyToPrint = e.target.checked;
        entries.load();
      }
    }), ' ', t('only_to_print_filter')), invalidCount > 0 && m("label", {
      className: "ChampisSocietyPage-onlyToPrintFilter"
    }, m("input", {
      type: "checkbox",
      checked: this.onlyInvalid,
      onchange: e => {
        this.onlyInvalid = e.target.checked;
        entries.load();
      }
    }), ' ', t('only_invalid_filter', {
      count: invalidCount
    })), phyla.length > 0 && m("label", {
      className: "ChampisSocietyPage-phylumFilter"
    }, t('phylum_filter_label'), ' ', m("select", {
      className: "FormControl",
      value: this.phylumFilter,
      onchange: e => {
        this.phylumFilter = e.target.value;
        entries.load();
      }
    }, m("option", {
      value: ""
    }, t('phylum_filter_all')), phyla.map(phylum => m("option", {
      value: phylum
    }, phylumLabel(phylum))), hasNoPhylum && m("option", {
      value: NO_PHYLUM
    }, t('phylum_filter_none')))), hasNoRange && m("div", {
      className: "ChampisSocietyPage-noRangeFilter"
    }, m("label", null, t('no_range_filter_label'), " ", m("strong", null, this.noRangeMin), " \u2013 ", m("strong", null, this.noRangeMax)), m("div", {
      className: "ChampisSocietyPage-noRangeSlider"
    }, m("div", {
      className: "ChampisSocietyPage-noRangeTrack"
    }), m("div", {
      className: "ChampisSocietyPage-noRangeFill",
      style: noRangeFillStyle || undefined
    }), m("input", {
      className: "ChampisSocietyPage-noRangeInput",
      type: "range",
      min: this.noBoundsMin,
      max: this.noBoundsMax,
      "aria-label": extractText(t('no_range_from_placeholder')),
      value: this.noRangeMin,
      oninput: e => {
        // Never past the other handle's own current value -
        // without this the two thumbs can cross and swap
        // visual position mid-drag, confusing which one is
        // "min" vs "max".
        this.noRangeMin = Math.min(parseInt(e.target.value, 10), this.noRangeMax);
        entries.reloadSoon();
      }
    }), m("input", {
      className: "ChampisSocietyPage-noRangeInput",
      type: "range",
      min: this.noBoundsMin,
      max: this.noBoundsMax,
      "aria-label": extractText(t('no_range_to_placeholder')),
      value: this.noRangeMax,
      oninput: e => {
        this.noRangeMax = Math.max(parseInt(e.target.value, 10), this.noRangeMin);
        entries.reloadSoon();
      }
    }))))), entries.items.length > 0 && m("ul", {
      className: "ChampisSocietyPage-entries"
    }, entries.items.map(entry => this.entryRow(entry))), entries.error ? m("div", {
      className: "ChampisExpoPage-error"
    }, m(Placeholder, {
      text: t('entries_error')
    }), m(Button, {
      className: "Button",
      onclick: () => entries.items.length > 0 ? entries.refresh() : entries.load()
    }, t('retry_button'))) : !entries.loading && entries.items.length === 0 && summary && m(Placeholder, {
      text: t(summary.count === 0 ? 'cartotheque_empty' : searched ? 'search_no_results' : 'filters_no_results')
    }), m(ListEnd, {
      list: entries
    }));
  }
  entryRow(entry) {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.cartotheque_page.${key}`, params);
    };
    const display = entry.speciesDisplay();
    const toxicity = entry.toxicity();
    const suggested = entry.suggestedSpecies();
    const conflicts = entry.suggestionConflicts() || {
      existing: [],
      alsoSuggested: []
    };
    const apiUrl = app.forum.attribute('apiUrl');
    return m("li", {
      key: entry.id(),
      className: "ChampisSocietyPage-entry"
    }, m("div", {
      className: "ChampisSocietyPage-entryBody"
    }, m("div", {
      className: "ChampisSocietyPage-entryName"
    }, entry.no() && m("span", {
      className: "ChampisSocietyPage-entryNo"
    }, entry.no()), display ? m("span", null, m("em", null, display.canonicalName || display.scientificName), " ", display.author) : t('unknown_species'), entry.toPrint() && m("span", {
      className: "ChampisSocietyPage-entryToPrint",
      title: extractText(t('to_print_label'))
    }, m("i", {
      className: "icon fas fa-print",
      "aria-hidden": "true"
    })), entry.description() && m("span", {
      className: "ChampisSocietyPage-entryHasDescription",
      title: extractText(t('has_description_label'))
    }, m("i", {
      className: "icon fas fa-qrcode",
      "aria-hidden": "true"
    }))), m("div", {
      className: "ChampisSocietyPage-entryMeta"
    }, display && display.phylum && m("span", {
      className: "ChampisSocietyPage-entryPhylum"
    }, phylumLabel(display.phylum)), display && display.family && m("span", null, t('family_label', {
      family: display.family
    })), entry.vernacularName() && m("span", null, entry.vernacularName()), toxicity && m("span", {
      className: "ChampisSocietyPage-entryToxicity"
    }, toxicity.svgUrl() && m("img", {
      className: "ChampisSocietyPage-toxicityIcon",
      src: toxicity.svgUrl(),
      alt: ""
    }), toxicity.name())), suggested && m("div", {
      className: "ChampisSocietyPage-entrySuggestion"
    }, m("i", {
      className: "icon fas fa-triangle-exclamation",
      "aria-hidden": "true"
    }), t('suggested_species_label'), " ", m("em", null, suggested.canonicalName || suggested.scientificName), m("a", {
      onclick: () => this.applySuggestedSpecies(entry)
    }, t('apply_suggestion_button')), conflicts.existing.length > 0 && m("span", {
      className: "ChampisSocietyPage-entryDuplicate"
    }, m("i", {
      className: "icon fas fa-clone",
      "aria-hidden": "true"
    }), t('duplicate_existing', {
      cards: cardLabels(conflicts.existing)
    })), conflicts.alsoSuggested.length > 0 && m("span", {
      className: "ChampisSocietyPage-entryDuplicate"
    }, m("i", {
      className: "icon fas fa-clone",
      "aria-hidden": "true"
    }), t('duplicate_also_suggested', {
      cards: cardLabels(conflicts.alsoSuggested)
    })))), m("div", {
      className: "ChampisSocietyPage-rowActions"
    }, entry.description() && m("a", {
      "aria-label": extractText(t('view_description_button')),
      title: extractText(t('view_description_button')),
      href: app.route('cartothequeEntryDescription', {
        id: entry.id()
      }),
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("i", {
      className: "icon fas fa-qrcode",
      "aria-hidden": "true"
    })), m("a", {
      "aria-label": extractText(t('download_pdf_button')),
      title: extractText(t('download_pdf_button')),
      href: `${apiUrl}/champis-exhibitions-cartotheque-entries/${entry.id()}/pdf`,
      target: "_blank",
      rel: "noopener noreferrer",
      onclick: e => {
        // Same cache-busting reasoning as the cartothèque section's
        // own download buttons above - an edit (species, layout, ...)
        // followed by re-opening this same URL could otherwise show a
        // stale cached PDF from before the edit.
        e.currentTarget.href = `${apiUrl}/champis-exhibitions-cartotheque-entries/${entry.id()}/pdf?_=${Date.now()}`;
      }
    }, m("i", {
      className: "icon fas fa-file-pdf",
      "aria-hidden": "true"
    })), m("a", {
      "aria-label": extractText(t('edit_entry_button')),
      title: extractText(t('edit_entry_button')),
      onclick: () => this.openEditEntryModal(entry)
    }, m("i", {
      className: "icon fas fa-pencil-alt",
      "aria-hidden": "true"
    })), m("a", {
      "aria-label": extractText(t('delete_entry_button')),
      title: extractText(t('delete_entry_button')),
      onclick: () => this.deleteEntry(entry)
    }, m("i", {
      className: "icon fas fa-trash",
      "aria-hidden": "true"
    }))));
  }
  openAddEntryModal() {
    app.modal.show(CartothequeEntryEditModal, {
      society: this.society,
      toxicities: this.toxicities || [],
      onsaved: () => this.entries.refresh()
    });
  }
  openEditEntryModal(entry) {
    app.modal.show(CartothequeEntryEditModal, {
      society: this.society,
      entry,
      toxicities: this.toxicities || [],
      onsaved: () => this.entries.refresh()
    });
  }
  deleteEntry(entry) {
    const t = key => app.translator.trans(`champis-exhibitions.forum.cartotheque_page.${key}`);
    if (!confirm(extractText(t('delete_entry_confirm')))) return;

    // Loaded again where they are: the card gone, the next one in.
    entry.delete().then(() => this.entries.refresh());
  }

  // Applies a suggestion from champis-exhibitions:check-cartotheque-species
  // (entry.suggestedSpecies()) in one click - a plain save() with the
  // suggested gbifId, exactly what re-picking that same species by hand
  // through the edit modal would send. gbifId's own .set() (server-side)
  // recomputes canonicalName/scientificName/author/family from the new id
  // and clears suggestedSpecies back to null, so no extra fields need
  // sending here beyond gbifId/toPrint - the refresh picks up all of it,
  // the other cards' conflicts too.
  //
  // A card of that species already there: asked first (see
  // CartothequeEntryResource::suggestionConflicts()) - the société may
  // rather delete this card.
  applySuggestedSpecies(entry) {
    const suggested = entry.suggestedSpecies();
    if (!suggested) return;
    const existing = entry.suggestionConflicts()?.existing || [];
    if (existing.length > 0) {
      const message = app.translator.trans('champis-exhibitions.forum.cartotheque_page.duplicate_confirm', {
        species: suggested.canonicalName || suggested.scientificName,
        cards: cardLabels(existing)
      });
      if (!confirm(extractText(message))) return;
    }
    entry.save({
      gbifId: suggested.gbifId,
      toPrint: true
    }).then(() => this.entries.refresh());
  }
  downloadFlaggedCards() {
    // A plain <a href> download gives no completion callback to reload
    // the cards from, but the batch endpoint clears to_print
    // server-side as a side effect of building the PDF - without this,
    // the fa-print flags in the list would keep showing stale state until
    // the next full page load. Approximate, not exact: just long enough
    // for the request and the browser's download prompt to complete in
    // practice.
    setTimeout(() => this.entries.refresh(), 1500);
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/CartothequePage', CartothequePage);